import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { jsonFetch } from 'ofe-common/utilities';
import serverFetch from '@globals/ServerFetch';
import { useAppStore } from '@globals/stores/app';
import { useUserStore } from '@globals/stores/user';
import useServices from '@customer/stores/composables/useServices';
import {
	ServicesFilter,
	ServicesObject,
	ServicesImportantMessage,
	ServicesFilterGroup,
	ServicesStats,
	ServicesUserActivityStats,
	ServicesContactPerson,
	ServicesFilterBackend,
	APMessages,
} from '@customer/types/Services';


export const useServicesStore = defineStore('services', () => {
	// --- state ---
	const services = ref<ServicesObject>({
		locations: {},
		tree: [] as ServicesFilterGroup[],
		stats: {} as ServicesStats,
		userActivityStats: {
			userActivityLoaded: false,
			userActivityDays: 30,
			matchedRfps: 0,
			boughtRfps: 0,
			matchedInstantRfps: 0,
		} as ServicesUserActivityStats,
		customer: {
			extendedLimit: true, // isCustomerQualifiedForHigherFilterLimit
		},
		contactPersons: [] as ServicesContactPerson[],
		operators: [] as ServicesContactPerson[],
		selectedFilters: [] as ServicesFilter[],
	});
	const processResult = ref<ServicesFilter | object>({});
	const itemType = ref('');
	const apiNamespace = ref(useAppStore().isAdminModule ? 'admin' : 'myoferteo');
	const companyName = ref('');
	const displaySaveChanges = ref(false);
	const displayAPToggler = ref(false);
	const isNewCategory = ref(false);
	const currentPage = ref('');
	const selectedCategoryGroupId = ref(0);
	const groupsToRemove = ref<{
		isSelected: boolean;
		categoryId: number;
		categoryName: string;
	}[]>([]);
	const selectedFilterIndex = ref(0);
	const isEditMatching = ref(false);
	const isEditLocations = ref(false);
	const isFreshCategory = ref(false);
	const suspendFilterStatus = ref('');
	const filterToRemove = ref({
		id: 0,
		type: '',
		categoryId: 0,
	});
	const isFilterDirty = ref(false);
	const isInsideFilterDetails = ref(false);
	const pricingCategoryId = ref(0);
	const customerId = ref(useAppStore().isAdminModule ? Number(useRoute()?.params?.id) : useUserStore().customerId);
	const testOFE35297 = ref<'A' | 'B' | 'C' | undefined>(undefined);
	const isRecommendationFeedbackModalVisible = ref(false);
	const notifications = ref({
		recommendation: {
			type: '',
			title: '',
			message: '',
			button: '',
			dismissButton: '',
			moreInfoLink: '',
			isVisible: false,
		},
	});
	const invalidStats = ref(false);
	const isLoading = ref(false);
	const isFreeRecommendationConfirmationModalVisible = ref(false);
	const potentialMatches = ref(-1);
	const visibilityTenPercentDiscountConfirmationModal = ref(false);
	const isAutoGenerateChatMessages = ref(false);
	const automaticFilterFromRfp = ref(false);
	const inExtraMatching = ref(false);
	const isExtraMatchingOnCurrentUser = ref(false);
	const messagesInAP = ref<APMessages>({
		toggle: null,
		message: '',
		useDefaultMessage: false,
	});
	const isAutomaticFastTrack = ref(true);

	// --- actions ---
	const { growTree, getContactPersons, getOperators, clearStatsCount, process, statsCount, groupLimitRecalculate } = useServices(services, processResult);
	async function getServicesList() {
		const { isAdminModule } = useAppStore();
		const result: ServicesFilterGroup[] = [];

		const rsvp = await serverFetch(
			`/${apiNamespace.value}/get-filter-list-new.ofer?includeLocations=${String(
				!services.value.locations.length,
			)}&matchingFilter.idCustomer=${customerId.value}`,
		);

		if (rsvp?.actionErrors?.length) {
			document.location.href = '/';

			return undefined;
		}

		if (!services.value.locations.length) {
			services.value.locations = await growTree();
		}

		services.value.contactPersons = [...getContactPersons(rsvp.contactPersons)];

		if (isAdminModule) {
			companyName.value = rsvp.customer[0].companyName;
		}

		services.value.operators = getOperators(rsvp.kam);

		clearStatsCount();

		for (const filterGroup of rsvp.filterGroups) {
			if (!filterGroup.filters.length) {
				continue;
			}

			if (!filterGroup.active) {
				services.value.stats.hasInactiveFilterGroups = true;
			}

			const notUndefined = <TValue>(value: TValue | undefined): value is TValue => value !== undefined;
			const filters = filterGroup.filters.map((filter: ServicesFilterBackend) => {
				const processed = process({ filterDetails: filter });
				if (processed) {
					statsCount(processed);
				}
				return processed || undefined;
			}).filter(notUndefined);

			const automaticFilter = filters.find(({ type }) => type === 'automatic');
			let automaticHasErrors = false;
			if (automaticFilter && automaticFilter.automaticRecommendations) {
				const automatic = automaticFilter.automaticRecommendations;
				automaticHasErrors = automatic.notEnoughBalance || automatic.needsModeration || !automatic.isCategoryVerified;
			}

			const group = {
				name: filterGroup.categoryName,
				categoryName: filterGroup.categoryName,
				categoryParentName: filterGroup.filters[0].categoryListForView.split(' / ')[0],
				categoryId: filterGroup.idCategory,
				isActive: filterGroup.active,
				isInitiallyActive: filterGroup.active,
				isAutoGenerateChatMessages: rsvp.autoGenerateChatMessages,
				automatic: {
					has: filterGroup.apEnabled,
					canBeEnabled: filterGroup.apCanBeEnabled,
					hasErrors: automaticHasErrors,
				},
				quickContact: {
					has: filterGroup.skEnabled,
					canBeEnabled: true,
				},
				isHidden: false,
				isApEnabled: filterGroup.apEnabled,
				isSkEnabled: filterGroup.skEnabled,

				isVerified: filterGroup.filters[0].filterCategories[0].verified as boolean,
				filters,
				userActivityStats: {} as ServicesUserActivityStats,
				isPricingEnabled: filterGroup.isPricingEnabled,
			} as ServicesFilterGroup;
			result.push(group);
		}

		services.value.tree = [...result];

		groupLimitRecalculate();

		invalidStats.value = services.value.stats.needsModeration || services.value.stats.notEnoughBalance;
	}

	async function loadInExtraMatching() {
		const { isAdminModule, hasFlag } = useAppStore();
		try {
			const showToggleResponse = isAdminModule ? await jsonFetch<{ value: boolean }>(`/admin/in-extra-matching.do?idCustomer=${customerId.value}`, { method: 'GET' }) : undefined;
			const showToggleThroughFlag = hasFlag('inExtraMatching');
			isExtraMatchingOnCurrentUser.value = (isAdminModule && showToggleResponse?.value) || (!isAdminModule && showToggleThroughFlag);

			const result = await jsonFetch<{ value: boolean }>(`${isAdminModule ? '/admin/extra-matching-toggle-value' : '/myoferteo/extra-matching-toggle-value'}.do${isAdminModule ? '?idCustomer=' + customerId.value : ''}`, {
				method: 'GET',
			});
			inExtraMatching.value = result.value;
		} catch (error) {
			console.error('Error loading inExtraMatching:', error);
		}
	}

	async function setInExtraMatching() {
		const { isAdminModule } = useAppStore();
		const payload = inExtraMatching.value;
		const errorMessages = { 'TypeError: Failed to fetch': 'Nie można połączyć się z serwerem. Sprawdź połączenie internetowe.' };
		try {
			const response = await jsonFetch<{ success: boolean }>(`${isAdminModule ? '/admin/set-extra-matching-toggle-value' : '/myoferteo/set-extra-matching-toggle-value'}/${Boolean(payload)}.do`, {
				method: 'POST',
				...(isAdminModule && {
					query: {
						idCustomer: customerId.value,
					},
				}),
			});
			if (!response.success) {
				throw new Error(response.responseMessage);
				// return [null, 'Nie można ustawić wartości'];
			}
			return [response.success, null];
		} catch (error) {
			console.error('Error setting inExtraMatching:', error);
			return [null, error || errorMessages[error]];
		}
	}

	function setTestOFE35297() {
		testOFE35297.value = 'A';
	}

	function selectedFilters({ selectedFilter, servicesObject }: { selectedFilter: ServicesFilter; servicesObject: ServicesObject }): ServicesFilter[] {
		// TODO: Sprawdź wywołania tej metody i zobacz, czy wszędzie tam nie trzeba dać value.
		const selectedFilterObject = selectedFilter.category ? selectedFilter : selectedFilter.value;

		const { categoryId } = selectedFilterObject.category;
		const foundGroup = servicesObject.tree.find((group) => group.categoryId === categoryId);
		const foundFilter = foundGroup?.filters.find((filter) => filter.id === selectedFilterObject.id);
		return foundFilter ? [selectedFilterObject, foundFilter] : [];
	}

	function setNotifications(apImportantMessage: ServicesImportantMessage) {
		notifications.value.recommendation = {
			type: apImportantMessage.type || 'info',
			title: apImportantMessage.title,
			message: apImportantMessage.message.replace(/<br>/g, ''),
			button: apImportantMessage.buttonLabel || 'Dowiedz się więcej',
			dismissButton: apImportantMessage.dismissButtonLabel || 'Przypomnij później',
			moreInfoLink: apImportantMessage.moreInfoLink,
			isVisible: true,
		};
	}

	function labelClasses(filter?: ServicesFilter) {
		if (!filter || !filter.isActive) {
			return 'bg-[#F2F2F2] text-[#666666]';
		}
		if (isFilterInactive(filter)) {
			return 'bg-[#FBEAE8] text-[#DA291C]';
		}
		if (filter.tsSuspendedEnd && isSuspended(filter)) {
			return 'bg-[#E7EFFB] text-[#1462D3]';
		}
		return 'bg-[#E7F3EF] text-[#0E875F]';
	}

	function isFilterInactive(filter: ServicesFilter) {
		return filter.type === 'automatic' && (
			filter.notEnoughPoints
			|| filter.finalMarkBelowApThreshold
			|| filter.weeklyLimitReached
		);
	}

	function isSuspended(filter: ServicesFilter) {
		return filter.isActive
			&& filter.tsSuspendedEnd
			&& filter.tsSuspendedEnd > Date.now()
			&& (filter.type === 'automatic' || filter.type === 'quick');
	}

	function setSelectedFilters(filterToEdit: ServicesFilter) {
		services.value.selectedFilters = [{ ...filterToEdit }];
	}

	// --- getters ---
	const selectedFilterGroup = computed((): ServicesFilterGroup | undefined => {
		if (!selectedCategoryGroupId.value) {
			return undefined;
		}
		return services.value.tree.find((group) => group.categoryId === selectedCategoryGroupId.value);
	});

	/**
	 * Aktualnie edytowany filtr
	 */
	const selectedFilter = computed((): ServicesFilter | undefined => {
		if (!services.value.selectedFilters.length) {
			return undefined;
		}
		return services.value.selectedFilters[selectedFilterIndex.value || 0].value;
	});

	return {
		// state
		services,
		processResult,
		itemType,
		apiNamespace,
		companyName,
		displaySaveChanges,
		displayAPToggler,
		isNewCategory,
		currentPage,
		selectedCategoryGroupId,
		groupsToRemove,
		selectedFilterIndex,
		isEditMatching,
		isEditLocations,
		isFreshCategory,
		suspendFilterStatus,
		filterToRemove,
		isFilterDirty,
		isInsideFilterDetails,
		pricingCategoryId,
		customerId,
		testOFE35297,
		isRecommendationFeedbackModalVisible,
		notifications,
		invalidStats,
		isLoading,
		isFreeRecommendationConfirmationModalVisible,
		potentialMatches,
		visibilityTenPercentDiscountConfirmationModal,
		isAutoGenerateChatMessages,
		automaticFilterFromRfp,
		inExtraMatching,
		isExtraMatchingOnCurrentUser,
		messagesInAP,
		isAutomaticFastTrack,
		// actions
		getServicesList,
		loadInExtraMatching,
		setInExtraMatching,
		setTestOFE35297,
		selectedFilters,
		setNotifications,
		labelClasses,
		isFilterInactive,
		isSuspended,
		setSelectedFilters,
		// getters
		selectedFilterGroup,
		selectedFilter,
	};
});
